import React, { useEffect, useMemo, useRef, useState } from "react";
import { HelpCircle, Plus, Settings, Sparkles, X } from "lucide-react";
import { Badge, Button, Combobox, Input, Label, Textarea, Tooltip } from "@/components/ui";
import { Blog, BlogRewriteArticleContext } from "@/services/bedrock";
import { cn } from "@/lib/utils";
import {
  emptyBlogSection,
  moveItem,
  normalizeBlogBody,
  parseTagInput,
  parseTags,
  ThemeOption,
  withCurrentThemeOption,
} from "../blogBody";
import { BlogSectionBlocks } from "./BlogSectionBlocks";
import {
  BlogRewriteField,
  BlogRewritePanel,
  BlogRewriteResult,
  rewriteApiField,
  RewriteHoverButton,
  RewriteHoverRegion,
} from "./BlogRewritePanel";
import { beginPointerReorder, ReorderGhost, ReorderGhostOverlay } from "./pointerReorder";

type Props = {
  draft: EditableBlog;
  readOnly: boolean;
  blogId?: number;
  themeOptions: ThemeOption[];
  canChangeTheme: boolean;
  themeSettingsUrl?: string;
  onChange: (patch: Partial<EditableBlog>) => void;
  onSectionHover?: (index: number | null, blockIndex?: number) => void;
  onSectionExpand?: (index: number, blockIndex?: number) => void;
  onSectionInputBlur?: () => void;
  onSectionDrag?: (index: number | null) => void;
  onPartHover?: (part: "title" | "subtitle" | "excerpt" | "tags" | "takeaways" | null) => void;
  onPartFocus?: (part: "title" | "subtitle" | "excerpt" | "tags" | "takeaways" | null) => void;
  onPreviewSection?: (sectionIndex: number, section: Blog.Section | null) => void;
  onPreviewField?: (patch: Partial<EditableBlog> | null) => void;
};

export type EditableBlog = {
  title: string;
  subtitle: string;
  metaDescription: string;
  excerpt: string;
  tags: string[];
  themeId: number | null;
  body: Blog.Body;
};

export const toEditableBlog = (blog: Blog): EditableBlog => ({
  title: blog.title ?? "",
  subtitle: blog.subtitle ?? "",
  metaDescription: blog.metaDescription ?? "",
  excerpt: blog.excerpt ?? "",
  tags: blog.tags ?? [],
  themeId: blog.contentPageThemeId ?? null,
  body: normalizeBlogBody(blog.body),
});

/**
 * Decides what the editor should show when a fresh copy of the blog arrives.
 *
 * A blog is created empty and filled in asynchronously, so the editor is seeded twice: once from
 * the placeholder record while the article is being written, then again from the finished article.
 * Once the Player can actually type, their in-progress edits win over later refreshes.
 */
export const nextEditorDraft = (
  next: Blog,
  current: EditableBlog | undefined,
  seededFrom: Blog | undefined,
  reseedEditor = false
): EditableBlog => {
  const replacingPlaceholder = seededFrom?.status === "GENERATING";
  const keepCurrent = !!current && !replacingPlaceholder && !reseedEditor;
  return keepCurrent ? current! : toEditableBlog(next);
};

const FieldLabelWithTooltip: React.FC<{ htmlFor: string; label: string; tooltip: string }> = ({
  htmlFor,
  label,
  tooltip,
}) => (
  <div className="flex items-center gap-1.5 min-w-0">
    <Label htmlFor={htmlFor}>{label}</Label>
    <Tooltip>
      <Tooltip.Trigger
        render={
          <button
            type="button"
            className="inline-flex items-center text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
            aria-label={`About ${label.toLowerCase()}`}
          >
            <HelpCircle className="size-3.5" />
          </button>
        }
      />
      <Tooltip.Content className="max-w-64">{tooltip}</Tooltip.Content>
    </Tooltip>
  </div>
);

const fieldPatch = (field: BlogRewriteField, value: string | string[]): Partial<EditableBlog> => {
  if (field === "tags") return { tags: parseTagInput(value) };
  return { [rewriteApiField(field)]: String(value) };
};

const previousValueFor = (draft: EditableBlog, field: BlogRewriteField): string | string[] => {
  if (field === "tags") return draft.tags;
  return draft[rewriteApiField(field)];
};

const FieldHeader: React.FC<{
  children: React.ReactNode;
  canRewrite?: boolean;
  rewriteLabel: string;
  rewriteActive?: boolean;
  onRewrite?: () => void;
}> = ({ children, canRewrite, rewriteLabel, rewriteActive, onRewrite }) => (
  <div className="flex items-center gap-1.5">
    <div className="min-w-0 flex-1">{children}</div>
    {canRewrite && onRewrite && <RewriteHoverButton label={rewriteLabel} active={rewriteActive} onClick={onRewrite} />}
  </div>
);

const TagChipsInput: React.FC<{
  tags: string[];
  disabled?: boolean;
  onChange: (tags: string[]) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}> = ({ tags, disabled, onChange, onFocus, onBlur }) => {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const addTags = (raw: string) => {
    const incoming = parseTags(raw);
    if (incoming.length === 0) return;

    const existing = new Set(tags.map(tag => tag.toLowerCase()));
    const next = [...tags];
    incoming.forEach(tag => {
      if (existing.has(tag.toLowerCase())) return;
      existing.add(tag.toLowerCase());
      next.push(tag);
    });
    onChange(next);
    setValue("");
  };

  const removeTag = (index: number) => onChange(tags.filter((_, i) => i !== index));

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 min-h-9 w-full rounded-lg border border-input bg-white px-2.5 py-1.5",
        "focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-3",
        disabled && "opacity-50 pointer-events-none"
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map((tag, index) => (
        <Badge key={`${tag}-${index}`} variant="outline" className="text-sm px-2 py-0.5 inline-flex items-center gap-1">
          {tag}
          {!disabled && (
            <X
              size={13}
              className="cursor-pointer hover:text-foreground"
              onClick={event => {
                event.stopPropagation();
                removeTag(index);
              }}
            />
          )}
        </Badge>
      ))}
      <input
        ref={inputRef}
        id="blog-tags"
        value={value}
        disabled={disabled}
        placeholder={tags.length === 0 ? "Type a tag and press Enter" : undefined}
        className="flex-1 min-w-24 bg-transparent border-none outline-none text-sm placeholder:text-muted-foreground"
        onChange={event => setValue(event.target.value)}
        onFocus={onFocus}
        onBlur={() => {
          if (value.trim()) addTags(value);
          onBlur?.();
        }}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === "," || event.key === "Tab") {
            if (!value.trim()) return;
            event.preventDefault();
            addTags(value.replace(/,$/, ""));
          } else if (event.key === "Backspace" && !value && tags.length > 0) {
            removeTag(tags.length - 1);
          }
        }}
      />
    </div>
  );
};

export const BlogEditor: React.FC<Props> = ({
  draft,
  readOnly,
  blogId,
  themeOptions,
  canChangeTheme,
  themeSettingsUrl,
  onChange,
  onSectionHover,
  onSectionExpand,
  onSectionInputBlur,
  onSectionDrag,
  onPartHover,
  onPartFocus,
  onPreviewSection,
  onPreviewField,
}) => {
  const sections = draft.body.sections ?? [];
  const keyTakeaways = draft.body.keyTakeaways ?? [];
  const hasTakeaways = keyTakeaways.some(item => item.trim());
  const [takeawaysOpen, setTakeawaysOpen] = useState(hasTakeaways);
  const [sectionKeys, setSectionKeys] = useState<string[]>(() => sections.map(() => crypto.randomUUID()));
  const [openedSectionKeys, setOpenedSectionKeys] = useState<string[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragGhost, setDragGhost] = useState<ReorderGhost | null>(null);
  const [rewriteField, setRewriteField] = useState<BlogRewriteField | null>(null);
  const [reviewingRewrite, setReviewingRewrite] = useState(false);
  const sectionsRef = useRef(sections);
  const sectionKeysRef = useRef(sectionKeys);
  sectionsRef.current = sections;
  sectionKeysRef.current = sectionKeys;

  useEffect(() => {
    setSectionKeys(current => {
      if (current.length === sections.length) return current;
      if (current.length < sections.length) {
        return [...current, ...Array.from({ length: sections.length - current.length }, () => crypto.randomUUID())];
      }
      return current.slice(0, sections.length);
    });
  }, [sections.length]);

  const updateSections = (next: Blog.Section[], nextKeys?: string[]) => {
    if (nextKeys) setSectionKeys(nextKeys);
    onChange({ body: { ...draft.body, sections: next } });
  };

  const updateSection = (index: number, next: Blog.Section) => {
    updateSections(sections.map((section, i) => (i === index ? next : section)));
  };

  const addSectionAt = (index: number) => {
    const key = crypto.randomUUID();
    const next = [...sections];
    next.splice(index, 0, emptyBlogSection());
    const nextKeys = [...sectionKeys];
    nextKeys.splice(index, 0, key);
    setOpenedSectionKeys(current => [...current, key]);
    updateSections(next, nextKeys);
  };

  const addSection = () => addSectionAt(sections.length);

  const removeSection = (index: number) => {
    updateSections(
      sections.filter((_, i) => i !== index),
      sectionKeys.filter((_, i) => i !== index)
    );
  };

  const startDrag = (event: React.PointerEvent, index: number, card: HTMLElement) => {
    beginPointerReorder({
      event,
      card,
      index,
      itemSelector: "[data-blog-section]",
      onReorder: (from, to) => {
        const next = moveItem(sectionsRef.current, from, to);
        const nextKeys = moveItem(sectionKeysRef.current, from, to);
        sectionsRef.current = next;
        sectionKeysRef.current = nextKeys;
        updateSections(next, nextKeys);
      },
      onActiveChange: index => {
        setDragIndex(index);
        onSectionDrag?.(index);
      },
      onGhostChange: setDragGhost,
    });
  };

  const themeItems = useMemo(() => withCurrentThemeOption(themeOptions, draft.themeId), [themeOptions, draft.themeId]);

  const selectedTheme = useMemo(
    () => (draft.themeId == null ? null : themeItems.find(option => option.id === draft.themeId) ?? null),
    [draft.themeId, themeItems]
  );

  useEffect(() => {
    if (hasTakeaways) setTakeawaysOpen(true);
  }, [hasTakeaways]);

  const canRewrite = !readOnly && !!blogId;
  const fieldsReadOnly = readOnly || reviewingRewrite;
  const articleContext: BlogRewriteArticleContext = {
    title: draft.title,
    subtitle: draft.subtitle,
    excerpt: draft.excerpt,
    metaDescription: draft.metaDescription,
    tags: draft.tags,
    otherSections: sections,
  };

  const toggleFieldRewrite = (field: BlogRewriteField) => {
    const next = rewriteField === field ? null : field;
    setRewriteField(next);
    if (next == null) {
      onPreviewField?.(null);
      onPartFocus?.(null);
      return;
    }
    if (next !== "searchDescription") onPartFocus?.(next);
  };

  const closeFieldRewrite = () => {
    setReviewingRewrite(false);
    setRewriteField(null);
    onPreviewField?.(null);
    onPartFocus?.(null);
  };

  const applyFieldRewrite = (result: BlogRewriteResult) => {
    if (result.kind !== "field") return;
    onChange(fieldPatch(result.field, result.value));
    closeFieldRewrite();
  };

  const handleFieldReviewing = (value: string | string[] | Blog.Section | null) => {
    if (value != null && typeof value === "object" && !Array.isArray(value)) return;
    setReviewingRewrite(!!value);
    if (value == null || !rewriteField) {
      onPreviewField?.(null);
      return;
    }
    onPreviewField?.(fieldPatch(rewriteField, value));
  };

  const blurPart = () => {
    if (!rewriteField) onPartFocus?.(null);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/80 via-white to-sky-50/60 p-5 shadow-sm">
        <div className="flex flex-col gap-4">
          <RewriteHoverRegion
            className="flex flex-col gap-1.5"
            onMouseEnter={() => onPartHover?.("tags")}
            onMouseLeave={() => onPartHover?.(null)}
          >
            <FieldHeader
              canRewrite={canRewrite}
              rewriteLabel="Suggest tags with AI"
              rewriteActive={rewriteField === "tags"}
              onRewrite={() => toggleFieldRewrite("tags")}
            >
              <FieldLabelWithTooltip
                htmlFor="blog-tags"
                label="Tags"
                tooltip="Shown as topic chips above the title. They also appear as filters on the Player's blog home."
              />
            </FieldHeader>
            <TagChipsInput
              tags={draft.tags}
              disabled={fieldsReadOnly}
              onChange={tags => onChange({ tags })}
              onFocus={() => onPartFocus?.("tags")}
              onBlur={blurPart}
            />
          </RewriteHoverRegion>

          <RewriteHoverRegion
            className="flex flex-col gap-1.5"
            onMouseEnter={() => onPartHover?.("title")}
            onMouseLeave={() => onPartHover?.(null)}
          >
            <FieldHeader
              canRewrite={canRewrite}
              rewriteLabel="Suggest a title with AI"
              rewriteActive={rewriteField === "title"}
              onRewrite={() => toggleFieldRewrite("title")}
            >
              <Label htmlFor="blog-title" className="text-indigo-900/70">
                Title
              </Label>
            </FieldHeader>
            <Input
              id="blog-title"
              value={draft.title}
              disabled={fieldsReadOnly}
              placeholder="Give this blog a title"
              className="h-11 border-indigo-100 bg-white text-lg font-semibold"
              onFocus={() => onPartFocus?.("title")}
              onBlur={blurPart}
              onChange={e => onChange({ title: e.target.value })}
            />
          </RewriteHoverRegion>

          <RewriteHoverRegion
            className="flex flex-col gap-1.5"
            onMouseEnter={() => onPartHover?.("subtitle")}
            onMouseLeave={() => onPartHover?.(null)}
          >
            <FieldHeader
              canRewrite={canRewrite}
              rewriteLabel="Suggest a subtitle with AI"
              rewriteActive={rewriteField === "subtitle"}
              onRewrite={() => toggleFieldRewrite("subtitle")}
            >
              <Label htmlFor="blog-subtitle">Subtitle</Label>
            </FieldHeader>
            <Input
              id="blog-subtitle"
              value={draft.subtitle}
              disabled={fieldsReadOnly}
              placeholder="A short hook under the title"
              className="bg-white"
              onFocus={() => onPartFocus?.("subtitle")}
              onBlur={blurPart}
              onChange={e => onChange({ subtitle: e.target.value })}
            />
          </RewriteHoverRegion>

          <RewriteHoverRegion
            className="flex flex-col gap-1.5"
            onMouseEnter={() => onPartHover?.("excerpt")}
            onMouseLeave={() => onPartHover?.(null)}
          >
            <FieldHeader
              canRewrite={canRewrite}
              rewriteLabel="Suggest an excerpt with AI"
              rewriteActive={rewriteField === "excerpt"}
              onRewrite={() => toggleFieldRewrite("excerpt")}
            >
              <FieldLabelWithTooltip
                htmlFor="blog-excerpt"
                label="Excerpt"
                tooltip="Shown under the table of contents on the article, and on blog cards and listings when there is no subtitle."
              />
            </FieldHeader>
            <Textarea
              id="blog-excerpt"
              rows={2}
              value={draft.excerpt}
              disabled={fieldsReadOnly}
              placeholder="A short summary of this article"
              className="bg-white"
              onFocus={() => onPartFocus?.("excerpt")}
              onBlur={blurPart}
              onChange={e => onChange({ excerpt: e.target.value })}
            />
          </RewriteHoverRegion>

          {canChangeTheme && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="blog-theme">Theme</Label>
              <div className="flex flex-row items-center gap-2">
                <Combobox
                  id="blog-theme"
                  className="flex-1 min-w-0 w-full bg-white"
                  placeholder="Default Theme"
                  items={themeItems}
                  value={selectedTheme}
                  showClear
                  disabled={readOnly}
                  onValueChange={option => onChange({ themeId: option?.id ?? null })}
                  itemToStringLabel={option => option.label}
                />
                {themeSettingsUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-lg"
                    aria-label="Open theme settings"
                    onClick={() => window.open(themeSettingsUrl, "_blank")}
                  >
                    <Settings className="size-5" />
                  </Button>
                )}
              </div>
            </div>
          )}

          <RewriteHoverRegion className="flex flex-col gap-1.5">
            <FieldHeader
              canRewrite={canRewrite}
              rewriteLabel="Suggest a search description with AI"
              rewriteActive={rewriteField === "searchDescription"}
              onRewrite={() => toggleFieldRewrite("searchDescription")}
            >
              <FieldLabelWithTooltip
                htmlFor="blog-meta"
                label="Search description"
                tooltip="Used as the SEO description in search results. About 160 characters. If this is empty, search engines fall back to the excerpt."
              />
            </FieldHeader>
            <Textarea
              id="blog-meta"
              rows={2}
              value={draft.metaDescription}
              disabled={fieldsReadOnly}
              placeholder="About 160 characters"
              className="bg-white"
              onChange={e => onChange({ metaDescription: e.target.value })}
            />
            <span className="text-xs text-muted-foreground">{draft.metaDescription.length}/160</span>
          </RewriteHoverRegion>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="text-sm font-bold uppercase tracking-wider text-slate-500">Article</div>
        {sections.length === 0 && (
          <div className="rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/40 px-4 py-8 text-center text-sm text-indigo-800/70">
            Add a section to start shaping the article.
          </div>
        )}
        {sections.map((section, index) => (
          <BlogSectionBlocks
            key={sectionKeys[index] ?? `section-${index}`}
            section={section}
            sectionIndex={index}
            blogId={blogId}
            articleContext={{
              ...articleContext,
              otherSections: sections.filter((_, sectionIndex) => sectionIndex !== index),
            }}
            defaultOpen={openedSectionKeys.includes(sectionKeys[index])}
            readOnly={readOnly}
            dragging={dragIndex === index}
            onChange={next => updateSection(index, next)}
            onRemove={() => removeSection(index)}
            onHover={(index: number | null, blockIndex?: number) => {
              if (dragIndex != null) return;
              onSectionHover?.(index, blockIndex);
            }}
            onExpand={onSectionExpand}
            onInputBlur={onSectionInputBlur}
            onPreviewSection={section => onPreviewSection?.(index, section)}
            onMoveStart={startDrag}
          />
        ))}
        <Button
          type="button"
          variant="outline"
          disabled={readOnly}
          onClick={addSection}
          className="h-12 w-full border-dashed border-indigo-200 bg-indigo-50/60 text-indigo-800 hover:bg-indigo-100 hover:text-indigo-950"
        >
          <Plus className="size-4" />
          Add section
        </Button>
      </div>

      {takeawaysOpen ? (
        <div
          className="flex flex-col gap-2 rounded-2xl border border-amber-200 bg-amber-50/70 p-4"
          onMouseEnter={() => onPartHover?.("takeaways")}
          onMouseLeave={() => onPartHover?.(null)}
        >
          <Label htmlFor="blog-takeaways" className="inline-flex items-center gap-2 text-amber-900">
            <Sparkles className="size-4" />
            Key takeaways
          </Label>
          <Textarea
            id="blog-takeaways"
            rows={3}
            value={keyTakeaways.join("\n")}
            disabled={readOnly}
            placeholder="One takeaway per line"
            className="bg-white"
            onFocus={() => onPartFocus?.("takeaways")}
            onBlur={() => onPartFocus?.(null)}
            onKeyDown={event => {
              if (event.key === " " || event.code === "Space") event.stopPropagation();
            }}
            onChange={e =>
              onChange({
                body: { ...draft.body, keyTakeaways: e.target.value.split("\n") },
              })
            }
          />
        </div>
      ) : (
        !readOnly && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setTakeawaysOpen(true)}
            className="h-12 w-full border-dashed border-amber-200 bg-amber-50/60 text-amber-900 hover:bg-amber-100 hover:text-amber-950"
          >
            <Sparkles className="size-4" />
            Add key takeaways
          </Button>
        )
      )}
      <ReorderGhostOverlay ghost={dragGhost} />
      {canRewrite && blogId && rewriteField && (
        <BlogRewritePanel
          open
          blogId={blogId}
          scope={rewriteField}
          previousValue={previousValueFor(draft, rewriteField)}
          articleContext={articleContext}
          onRewritten={applyFieldRewrite}
          onReviewing={handleFieldReviewing}
          onDismiss={closeFieldRewrite}
        />
      )}
    </div>
  );
};
